import React from 'react';
import { Target, BookOpen, Lightbulb, GraduationCap, Clock, Compass, ChevronRight } from 'lucide-react';

/**
 * Composant amélioré pour afficher une carte d'objectif
 * Supporte les différentes phases (Ambition, Stratégie, Action)
 * et affiche des indicateurs de progression
 */
const ObjectifCard = ({ 
  step,
  title, 
  description, 
  icon = 'target', 
  isActive = false,
  isClickable = false,
  onClick = () => {},
  badge = null,
  data = null,
  
  // Nouveaux paramètres pour le modèle de données amélioré
  objectif = null,
  showProgress = false,
  progressValue = 0,
  phase = null,
  theme = null,
  chapitre = null
}) => {
  // Déterminer quelle icône afficher
  const getIcon = () => {
    switch(icon) {
      case 'book':
        return <BookOpen className={`h-10 w-10 ${isActive ? 'text-blue-600' : 'text-blue-400'} mb-3`} />;
      case 'lightbulb':
        return <Lightbulb className={`h-10 w-10 ${isActive ? 'text-yellow-600' : 'text-yellow-400'} mb-3`} />;
      case 'graduation':
        return <GraduationCap className={`h-10 w-10 ${isActive ? 'text-green-600' : 'text-green-400'} mb-3`} />;
      case 'compass':
        return <Compass className={`h-10 w-10 ${isActive ? 'text-blue-600' : 'text-blue-400'} mb-3`} />;
      case 'clock':
        return <Clock className={`h-10 w-10 ${isActive ? 'text-orange-600' : 'text-orange-400'} mb-3`} />;
      case 'target':
      default:
        return <Target className={`h-10 w-10 ${isActive ? 'text-purple-600' : 'text-purple-400'} mb-3`} />;
    }
  };

  // Obtenir la couleur en fonction de la phase
  const getPhaseColor = (phaseName) => {
    switch(phaseName) {
      case 'ambition':
        return 'bg-purple-100 text-purple-800';
      case 'stratégie':
        return 'bg-blue-100 text-blue-800';
      case 'action':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obtenir la couleur en fonction du thème
  const getThemeColor = (themeName) => {
    switch(themeName) {
      case 'Éducatif':
        return 'bg-blue-100 text-blue-800';
      case 'Professionnel':
        return 'bg-indigo-100 text-indigo-800';
      case 'Personnel':
        return 'bg-green-100 text-green-800';
      case 'Business':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Gérer le clic sur la carte
  const handleClick = () => {
    if (isClickable) {
      onClick(objectif || data);
    }
  };

  return (
    <div 
      className={`
        bg-white rounded-xl shadow-md transition-all duration-300
        ${isActive ? 'border-2 border-purple-300' : 'border border-gray-200 shadow-sm'} 
        p-5 relative
        ${isClickable ? 'cursor-pointer hover:shadow-lg hover:translate-y-[-2px]' : ''}
      `}
      onClick={handleClick}
      role={isClickable ? "button" : ""}
      tabIndex={isClickable ? 0 : -1}
      aria-label={isClickable ? `Sélectionner: ${title}` : ""}
    >
      {/* Numéro d'étape si présent */}
      {isActive && step && (
        <div className="absolute -top-3 -left-3 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
          {step}
        </div>
      )}
      
      {/* Contenu principal */}
      <div className="flex flex-col items-center text-center">
        {getIcon()}
        
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
        
        {description && (
          <p className="text-base text-gray-600 mb-3">
            {description}
          </p>
        )}
        
        {/* Affichage de la phase (si disponible) */}
        {phase && (
          <div className={`px-3 py-1 rounded-full text-xs font-medium mb-2 ${getPhaseColor(phase)}`}>
            {phase.charAt(0).toUpperCase() + phase.slice(1)}
          </div>
        )}
        
        {/* Affichage des badges/étiquettes */}
        <div className="flex flex-wrap gap-2 justify-center mt-1 mb-2">
          {badge && (
            <div className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
              {badge}
            </div>
          )}
          
          {theme && (
            <div className={`inline-block text-xs px-2 py-1 rounded-full ${getThemeColor(theme)}`}>
              {theme}
            </div>
          )}
          
          {chapitre && (
            <div className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
              {chapitre}
            </div>
          )}
        </div>
        
        {/* Barre de progression (si activée) */}
        {showProgress && (
          <div className="w-full mt-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progression</span>
              <span>{progressValue}%</span>
            </div>
            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-500"
                style={{ width: `${progressValue}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Bouton ou indicateur de cliquabilité */}
        {isClickable && (
          <div className="mt-3 text-xs text-purple-600 flex items-center justify-center">
            <span>Cliquer pour commencer</span>
            <ChevronRight className="h-3 w-3 ml-1" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectifCard;